"use client";

import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, BrainCircuit, Mail, Target, Award, Sparkles, Users } from "lucide-react";
import { CTA } from "./sections/cta";
import { Header } from "./sections/header";
import { Footer } from "./sections/footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Section } from "./section";
import { cn } from "@/lib/utils";

// FAQ Data
const FAQs = [
  {
    id: "1",
    question: "What is the purpose of this website?",
    answer:
      "This website is a place to help you find the best products and services in the world. We aim to provide comprehensive reviews and comparisons to help you make informed decisions.",
  },
  {
    id: "2",
    question: "How do I contact support?",
    answer:
      "You can contact support by email at support@example.com or by phone at 123-456-7890. ",
  },
  {
    id: "3",
    question: "How do I find the best products?",
    answer:
      "You can find the best products by searching for them on the search page or by browsing the categories. Our curated lists and expert reviews will guide you to the top choices.",
  },
  {
    id: "4",
    question: "Can I return a product?",
    answer:
      "Yes, you can return a product within 30 days of purchase. Please refer to our return policy for more details. Ensure the product is in its original condition and packaging.",
  },
  {
    id: "5",
    question: "Do you offer international shipping?",
    answer:
      "Yes, we offer international shipping to most countries. Shipping fees and delivery times may vary depending on the destination. ",
  },
  {
    id: "6",
    question: "How can I track my order?",
    answer:
      "You can track your order by logging into your account and visiting the order history page. You will also receive a tracking number via email once your order has shipped. Stay updated with real-time tracking.",
  },
  {
    id: "7",
    question: "What payment methods do you accept?",
    answer:
      "We accept various payment methods including credit cards, debit cards, PayPal, and bank transfers. Choose the most convenient option for you during checkout.",
  },
  {
    id: "8",
    question: "How do I reset my password?",
    answer:
      "You can reset your password by clicking on the 'Forgot Password' link on the login page and following the instructions. A password reset link will be sent to your registered email address.",
  },
  {
    id: "9",
    question: "Can I change my shipping address after placing an order?",
    answer:
      "Yes, you can change your shipping address before the order is shipped. Please contact our support team for assistance. Provide the new address details promptly to avoid any delays.",
  },
  {
    id: "10",
    question: "What is your privacy policy?",
    answer:
      "Our privacy policy outlines how we collect, use, and protect your personal information. You can read it on our privacy policy page. We are committed to safeguarding your privacy.",
  },
  {
    id: "11",
    question: "How do I leave a review for a product?",
    answer:
      "You can leave a review for a product by logging into your account, navigating to the product page, and clicking on the 'Write a Review' button. Share your experience to help others make informed decisions.",
  },
];

// Team Members Data
const teamMembers = [
  {
    name: "Sarah Johnson",
    role: "CEO & Founder",
    bio: "AI visionary with 15+ years of experience in enterprise technology transformation.",
    image: "", // Will use the fallback
    initials: "SJ"
  },
  {
    name: "David Chen",
    role: "CTO",
    bio: "Machine learning expert with a background in developing scalable AI systems.",
    image: "", // Will use the fallback
    initials: "DC"
  },
  {
    name: "Priya Patel",
    role: "Head of Product",
    bio: "Product strategist focused on creating intuitive AI experiences for enterprises.",
    image: "", // Will use the fallback
    initials: "PP"
  },
  {
    name: "Michael Thompson",
    role: "Lead AI Architect",
    bio: "Specializes in designing custom AI solutions for complex business challenges.",
    image: "", // Will use the fallback
    initials: "MT"
  },
];

export function AboutUsPage() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section with Video Background */}
        <div className="relative w-full h-screen">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-hidden dark:opacity-20"
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-full object-cover dark:[mask-image:radial-gradient(circle_at_center,white,transparent)]"
            >
              <source
                src="https://assets.aceternity.com/background-demo.mp4"
                type="video/mp4"
              />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background z-10"></div>
          </motion.div>
          
          <div className="relative z-20 h-full flex flex-col items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge 
                variant="outline" 
                className="mb-4 px-3 py-1 text-sm font-medium shadow-sm transition-colors hover:bg-accent"
              >
                About Kled.io
              </Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                Empowering Australian Enterprises with <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">Secure AI</span>
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-300">
                We provide a Remote Environment Ecosystem that enables end-to-end adoption 
                of AI/ML into Business Processes for Australian Enterprises with complete data privacy.
              </p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex flex-wrap gap-4 justify-center mt-8"
              >
                <motion.a
                  href="#origin-story"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex h-12 items-center justify-center rounded-md px-6 py-3 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 shadow-sm bg-white text-black hover:bg-gray-200"
                >
                  Learn More
                </motion.a>
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex h-12 items-center justify-center rounded-md px-6 py-3 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 shadow-sm bg-transparent text-white border border-white/30 hover:bg-white/10"
                >
                  Contact Us
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Origin Story */}
        <Section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="flex flex-col justify-center"
              >
                <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                  Our Origin Story
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    Kled.io was founded in 2020 by a team of AI specialists and business 
                    transformation experts who recognized a critical gap in the Australian 
                    enterprise landscape: the difficulty of implementing AI solutions at scale.
                  </p>
                  <p>
                    We observed that while many organizations understood the potential of AI, 
                    they struggled with the practical aspects of integration, security, and 
                    adoption. This inspired us to create a comprehensive ecosystem that bridges 
                    this gap.
                  </p>
                  <p>
                    Today, we're proud to be at the forefront of AI adoption in Australia, 
                    helping enterprises transform their operations through intelligent automation 
                    and data-driven insights.
                  </p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative h-[400px] overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 shadow-md"
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <BrainCircuit className="h-32 w-32 text-primary/40" />
                </div>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Mission, Vision, Values */}
        <Section className="bg-muted/30 py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mx-auto mb-12 max-w-3xl text-center"
            >
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Our Mission, Vision & Values
              </h2>
              <p className="text-lg text-muted-foreground">
                At Kled.io, we're driven by a clear purpose and guided by strong principles.
              </p>
            </motion.div>

            <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-all duration-200 hover:shadow-md"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Target className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Our Mission</h3>
                <p className="text-muted-foreground">
                  To democratize AI adoption across Australian enterprises by providing 
                  a secure, scalable, and user-friendly ecosystem that transforms business 
                  processes and drives innovation.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex flex-col rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-all duration-200 hover:shadow-md"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Our Vision</h3>
                <p className="text-muted-foreground">
                  To be the catalyst for AI-driven transformation in Australia, creating 
                  a future where every enterprise can harness the power of artificial 
                  intelligence to solve complex problems and create new opportunities.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="flex flex-col rounded-xl border border-border/50 bg-card p-6 shadow-sm transition-all duration-200 hover:shadow-md sm:col-span-2 lg:col-span-1"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Award className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Our Values</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="mr-2 text-primary">•</span>
                    <span><strong>Innovation:</strong> We constantly push boundaries and explore new possibilities.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-primary">•</span>
                    <span><strong>Integrity:</strong> We operate with transparency and ethical responsibility.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-primary">•</span>
                    <span><strong>Collaboration:</strong> We believe in the power of partnership and teamwork.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-primary">•</span>
                    <span><strong>Excellence:</strong> We strive for the highest quality in everything we do.</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Team Section */}
        <Section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mx-auto mb-12 max-w-3xl text-center"
            >
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Meet Our Team
              </h2>
              <p className="text-lg text-muted-foreground">
                The passionate experts behind Kled.io's innovation and success.
              </p>
            </motion.div>

            <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group flex flex-col items-center rounded-xl border border-border/50 bg-card p-6 text-center shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  <Avatar className="mb-4 h-24 w-24 border-2 border-border shadow-sm transition-all duration-200 group-hover:shadow-md">
                    <AvatarImage src={member.image} alt={member.name} />
                    <AvatarFallback className="text-lg">{member.initials}</AvatarFallback>
                  </Avatar>
                  <h3 className="mb-1 text-lg font-semibold text-foreground">{member.name}</h3>
                  <p className="mb-3 text-sm text-primary">{member.role}</p>
                  <p className="text-sm text-muted-foreground">{member.bio}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* FAQ Section */}
        <Section className="bg-muted/30 py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mx-auto mb-12 max-w-3xl text-center"
            >
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-muted-foreground">
                Find answers to common questions about Kled.io and our services.
              </p>
            </motion.div>

            <div className="mx-auto max-w-3xl">
              <Accordion type="single" collapsible className="w-full">
                {FAQs.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="mt-12 flex flex-col items-center rounded-lg bg-accent p-6 text-center shadow-sm max-w-3xl mx-auto">
              <h3 className="mb-2 text-lg font-semibold">Still have questions?</h3>
              <p className="mb-6 text-muted-foreground">
                Our team is here to help you with any additional questions or concerns.
              </p>
              <Button className="h-9 px-4 py-2 shadow-sm transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-ring focus:ring-offset-2">
                Contact Support
              </Button>
            </div>
          </div>
        </Section>

        {/* CTA Section */}
        <section className="py-16">
          <CTA />
        </section>
      </div>
      <Footer />
    </>
  );
}

export default AboutUsPage;
